import React from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Heart, Users, Brain, MessageSquare } from "lucide-react";

export default function RelationshipDynamicsSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="section-headline text-slate-900 mb-6">
            Deep Understanding of Your <span className="text-blue-500">Relationship Dynamics</span>
          </h2>
          
          <p className="body-text text-slate-600 max-w-3xl mx-auto">
            Go beyond surface-level insights with comprehensive analysis of how you connect, communicate, and grow together.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 hover:shadow-lg transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h3 className="feature-title text-slate-900 mb-3">Attachment Styles</h3>
              <p className="text-slate-600">
                Understand your attachment patterns and how they influence your relationship dynamics.
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 hover:shadow-lg transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="feature-title text-slate-900 mb-3">Personality Traits</h3>
              <p className="text-slate-600">
                Discover your core personality traits and how they manifest in your communication.
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 hover:shadow-lg transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <h3 className="feature-title text-slate-900 mb-3">MBTI Insights</h3>
              <p className="text-slate-600">
                Get nuanced MBTI analysis based on your actual communication patterns, not survey responses.
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 hover:shadow-lg transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h3 className="feature-title text-slate-900 mb-3">Love Languages & Conflict Styles</h3>
              <p className="text-slate-600">
                Identify your love languages and conflict resolution patterns through message analysis.
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Feature Highlight */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="section-headline text-slate-900 mb-4">
                See Your Growth Over Time
              </h3>
              <p className="body-text text-slate-600 mb-6">
                Track how your communication patterns evolve as you work on your relationship. WeSonder provides ongoing insights that help you measure real progress.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-slate-700">Communication Clarity</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm font-medium text-slate-700">Emotional Intelligence</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-sm font-medium text-slate-700">Relationship Health</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Communication Score</span>
                    <span className="text-2xl font-bold text-green-600">8.2/10</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: '82%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Emotional Support</span>
                    <span className="text-2xl font-bold text-blue-600">7.8/10</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Conflict Resolution</span>
                    <span className="text-2xl font-bold text-cyan-600">6.5/10</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-cyan-500 h-2 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 